import {
    infiniteQueryOptions,
    keepPreviousData,
    queryOptions,
    skipToken,
    useMutation,
    useQuery,
    type DefaultError,
    type QueryClient,
    type UseMutationResult,
} from '@tanstack/react-query';
import flatten from 'lodash/flatten';

import {
    EhrCreateForm,
    EhrDataListItem,
    EhrEntryCount,
    EhrReferralPatientSearchQuery,
    EhrSearchQuery,
} from 'chaika-frontend-types/frontend-cms';
import { EhrViewContext, SavedPaymentMethodStatus } from 'chaika-enums';

import { SubscriptionApi } from '@api/SubscriptionApi';
import { EhrApi } from '@api/EhrApi';
import { LastViewApi } from '@api/LastViewApi';
import { queryClient } from '@store/queryClient';
import { SavedPaymentMethodApi } from '@api/SavedPaymentMethodApi';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';
import { loadWithChunkAndFlatten } from '@utils/loadWithChunkAndFlatten';
import { STALE_TIME_FIVE_MINUTES_MS, STALE_TIME_ONE_MINUTE_MS } from '@utils/staleTime';

export const DEFAULT_EHR_LIST_LIMIT = 50;

export interface EhrListFilters {
    query?: string;
    ids?: string[];
    limit?: number;
}

export const ehrKeys = {
    all: ['ehrs'] as const,
    detail: (id: string | undefined) => [...ehrKeys.all, 'detail', id] as const,
    optionLists: () => [...ehrKeys.all, 'optionList'] as const,
    optionList: (filters: EhrListFilters) =>
        [...ehrKeys.optionLists(), { filters: normalizeEhrListFilters(filters) }] as const,
    lastViewed: (userId: string) => [...ehrKeys.all, 'lastViewed', userId] as const,
    referralLists: () => [...ehrKeys.all, 'list'] as const,
    referralPatients: (params: EhrReferralPatientSearchQuery) => [...ehrKeys.referralLists(), { params }] as const,
    byPolicy: (policyNumber: string) => [...ehrKeys.all, 'byPolicy', policyNumber] as const,
    duplicates: (params: EhrSearchQuery) => [...ehrKeys.all, 'duplicates', { params }] as const,
    entryCountLists: () => [...ehrKeys.all, 'entryCountList'] as const,
    entryCountList: (ehrIds: string[]) => [...ehrKeys.entryCountLists(), getUniqSortedIds(ehrIds)] as const,
    cards: (id: string | undefined) => [...ehrKeys.all, 'cards', id] as const,
    similar: (id: string | undefined) => [...ehrKeys.all, 'similar', id] as const,
    // serviceIds keep caller order: the response shape (and the first-wins share) depends on it
    servicePlans: (id: string | undefined, serviceIds: string[], actualOn: string, onlyAvailable: boolean) =>
        [
            ...ehrKeys.all,
            'servicePlans',
            id,
            { serviceIds: [...new Set(serviceIds)], actualOn, onlyAvailable },
        ] as const,
};

export const ehrQueryOptions = {
    detail: (id: string | undefined, viewContext?: EhrViewContext) =>
        queryOptions({
            queryKey: ehrKeys.detail(id),
            queryFn: id ? () => EhrApi.get(id, viewContext) : skipToken,
        }),
    list: (filters: EhrListFilters) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: ehrKeys.optionList(filters),
            queryFn: () => fetchEhrsByChunks(filters),
        }),
    cards: (id: string | undefined) =>
        queryOptions({
            queryKey: ehrKeys.cards(id),
            queryFn:
                id !== undefined
                    ? () => SavedPaymentMethodApi.find({ userId: id, status: SavedPaymentMethodStatus.Active })
                    : skipToken,
        }),
    similar: (id: string | undefined) =>
        queryOptions({
            queryKey: ehrKeys.similar(id),
            queryFn: id !== undefined ? () => EhrApi.findSimilar(id) : skipToken,
        }),
    servicePlans: (id: string | undefined, serviceIds: string[], actualOn: string, onlyAvailable: boolean) =>
        queryOptions({
            queryKey: ehrKeys.servicePlans(id, serviceIds, actualOn, onlyAvailable),
            queryFn:
                id !== undefined
                    ? () => EhrApi.getServicePlans(id, { serviceIds, actualOn, onlyAvailable })
                    : skipToken,
            enabled: serviceIds.length > 0 && Boolean(actualOn),
        }),
    lastViewed: (userId: string) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: ehrKeys.lastViewed(userId),
            queryFn: () => LastViewApi.getEhrList(userId),
            enabled: Boolean(userId),
        }),
    byPolicy: (policyNumber: string) =>
        queryOptions({
            queryKey: ehrKeys.byPolicy(policyNumber),
            queryFn: async (): Promise<EhrDataListItem[]> => {
                const subscriptions = await SubscriptionApi.find({ policyNumbers: [policyNumber] });
                const chunkedEhrIds = getUniqChunkedIds(subscriptions.map(({ ehrId }) => ehrId));

                if (chunkedEhrIds.length === 0) {
                    return [];
                }

                const responses = await Promise.all(chunkedEhrIds.map(ids => EhrApi.find({ ids: ids.join(',') })));

                return flatten(responses);
            },
            enabled: policyNumber.length > 0,
        }),
    duplicates: (params: EhrSearchQuery) =>
        queryOptions({
            staleTime: 0,
            queryKey: ehrKeys.duplicates(params),
            queryFn: () => EhrApi.find(params),
        }),
    entryCounts: (ehrIds: string[]) => {
        const normalizedIds = getUniqSortedIds(ehrIds);

        return queryOptions({
            staleTime: STALE_TIME_ONE_MINUTE_MS,
            placeholderData: keepPreviousData,
            queryKey: ehrKeys.entryCountList(normalizedIds),
            queryFn: () => fetchEhrsEntryCountByChunks(normalizedIds),
        });
    },
    referralPatients: (params: EhrReferralPatientSearchQuery) =>
        infiniteQueryOptions({
            staleTime: STALE_TIME_FIVE_MINUTES_MS,
            queryKey: ehrKeys.referralPatients(params),
            initialPageParam: { limit: 100, offset: 0 },
            queryFn: ({ pageParam }) =>
                EhrApi.searchReferralPatients({
                    ...params,
                    limit: pageParam.limit,
                    offset: pageParam.offset,
                }),
            getNextPageParam: (lastData, _, lastParams) => {
                const loadedCount = lastParams.offset + lastData.ehrs.length;
                return loadedCount < lastData.count
                    ? {
                          limit: lastParams.limit,
                          offset: lastParams.offset + lastParams.limit,
                      }
                    : undefined;
            },
            select: data => ({
                ehrs: data.pages.flatMap(page => page.ehrs) ?? [],
                count: data.pages[0]?.count ?? 0,
            }),
        }),
};

export function useEhr(id: string, viewContext?: EhrViewContext) {
    return useQuery(ehrQueryOptions.detail(id, viewContext));
}

export function useCreateEhr(): UseMutationResult<void, DefaultError, EhrCreateForm> {
    return useMutation({
        mutationFn: (ehr: EhrCreateForm) => EhrApi.create(ehr),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ehrKeys.optionLists() }),
    });
}

export function useUpdateEhrPhone(): UseMutationResult<void, DefaultError, { ehrId: string; phone: string }> {
    return useMutation({
        mutationFn: ({ ehrId, phone }) => EhrApi.update(ehrId, { phone }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ehrKeys.all }),
    });
}

// PatientSelect resolves the label of its closed (preselected) value by reading
// `ehrQueryOptions.list({ ids: [patientId] })`. When the user just picked an option, the
// corresponding EhrDataListItem is already cached under some other option-list query (the
// default list or the search). Copy it across so the per-ids query resolves from cache
// instead of round-tripping. Mirrors `primeUserListByIdsCache`.
export function primeEhrListByIdsCache(queryClient: QueryClient, ehrIds: string[]): void {
    if (ehrIds.length === 0) {
        return;
    }

    const targetKey = ehrQueryOptions.list({ ids: ehrIds }).queryKey;

    if (queryClient.getQueryData(targetKey)) {
        return;
    }

    const cachedEhrsById = new Map<string, EhrDataListItem>();
    const cachedListQueries = queryClient.getQueriesData<EhrDataListItem[]>({ queryKey: ehrKeys.optionLists() });
    for (const [, ehrs] of cachedListQueries) {
        ehrs?.forEach(ehr => cachedEhrsById.set(ehr.id, ehr));
    }

    const matched = ehrIds.map(id => cachedEhrsById.get(id)).filter((ehr): ehr is EhrDataListItem => ehr !== undefined);

    if (matched.length === ehrIds.length) {
        queryClient.setQueryData(targetKey, matched);
    }
}

function normalizeEhrListFilters({ query, ids, limit }: EhrListFilters): EhrListFilters {
    return {
        query: query || undefined,
        ids: ids ? getUniqSortedIds(ids) : undefined,
        limit,
    };
}

function fetchEhrsEntryCountByChunks(ehrIds: string[]): Promise<EhrEntryCount[]> {
    return loadWithChunkAndFlatten(ehrIds, ({ ids }) => EhrApi.getEhrsEntryCount(ids.split(',')));
}

async function fetchEhrsByChunks({ query, ids, limit }: EhrListFilters): Promise<EhrDataListItem[]> {
    const chunkedIds = ids ? getUniqChunkedIds(ids, 300) : [];

    if (chunkedIds.length === 0) {
        return EhrApi.find({ query, limit });
    }

    const responses = await Promise.all(chunkedIds.map(chunk => EhrApi.find({ query, ids: chunk.join(',') })));

    return flatten(responses);
}
